<template>
  <div class="profile-wrapper px-0 py-4 pa-sm-6 d-flex flex-column flex-md-row">
    <v-card 
      class="profile-sidebar rounded-lg"
      variant="outlined"
    >
      <v-toolbar class="profile-sidebar-header d-none d-md-flex">
        <v-toolbar-title>
          {{ profile.firstName }} {{ profile.lastName }}
        </v-toolbar-title>
      </v-toolbar>
      <v-tabs :model-value="path" direction="vertical" class="profile-tabs" data-locator="profile-page-navigation">
        <template v-for="section in PROFILE_SECTIONS" :key="section.key">
          <v-tab 
            :value="`/profile/${section.key}`"
            @click="$router.push('/profile/' + section.key).catch(() => {})"
          >
            <div class="d-flex align-center column-gap-2">
              <app-material-icon 
                :name="section.icon" 
                :filled="path === `/profile/${section.key}`"
                :class="{'text-primary': path === `/profile/${section.key}`}"
                class="profile-tabs-icon"
                size="xs"
                type="fa"
                weight="fal"
              />
              <span>{{ $t(section.title) }}</span>
              <template v-if="section.isNew">
                <AppFeatureTag tag="new" />
              </template>
              <template v-if="section.key === 'identity-checking' && !canCurrentUserSign">
                <v-badge
                  :model-value="true"
                  :offset-x="-6"
                  :offset-y="-10"
                  color="error"
                  dot
                />
              </template>
            </div>
          </v-tab>
          <v-divider />
        </template>
      </v-tabs>
    </v-card>
    <v-card variant="outlined" class="profile-view rounded-lg px-2 py-4 px-sm-2 py-sm-4" data-locator="profile-view">
      <v-window v-model="path">
        <v-window-item :value="path">
          <transition name="app-fade">
            <div :key="path">
              <router-view v-slot="{ Component }">
                <component :is="Component" />
              </router-view>
            </div>
          </transition>
        </v-window-item>
      </v-window>
    </v-card>
    <ProfileNavigationDrawer
      v-if="$vuetify.display.mobile"
      :profileSections="PROFILE_SECTIONS"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import AppFeatureTag from '@/common/AppFeatureTag.vue'
import ProfileNavigationDrawer from '@/profile/ProfileNavigationDrawer.vue'
import profileSections from '@/profile/profileSections'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'

export default {
  name: 'Profile',
  components: {
    AppFeatureTag,
    ProfileNavigationDrawer,
  },
  data () {
    return {
      PROFILE_SECTIONS: profileSections,
    }
  },
  computed: {
    ...mapGetters('user', ['canCurrentUserSign']),
    ...mapState('user', ['profile']),
    path () {
      const matchedSection = this.PROFILE_SECTIONS.find(section =>
        this.$route.path.startsWith(`/profile/${section.key}`), // handles dynamic urls
      )
      return matchedSection ? `/profile/${matchedSection.key}` : ''
    },
  },
  created () {
    this.$store.commit('global/' + GlobalMutationTypes.SET_BADGE_PROFILE_VISIBLE, !this.canCurrentUserSign)
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.profile-wrapper {
  gap: 24px;
  height: 100%;
  margin: 0 auto;
  max-width: 1280px;
  width: 100%;
}

.profile-sidebar {
  width: 100%;

  @media #{map-get($display-breakpoints, 'md-and-up')}{
    max-width: 325px;
  }

  &-header {
    background-color: white;
    border-bottom: 1px solid rgb(var(--v-theme-grey-lighten-4));
  }
}

.profile-view {
  flex-grow: 1;
}

.profile-tabs {
  .v-tab {
    display: flex;

    &.v-slide-group-item--active {
      background-color: rgb(var(--v-theme-brand-lighten-4));
      color: rgb(var(--v-theme-brand));
    }
  }
}
</style>
